import { LocaleLink } from "@/components/LocaleLink";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/seo/SEOHead";
import JsonLd from "@/components/seo/JsonLd";
import {
  ArrowRight,
  AlertTriangle,
  DollarSign,
  Users,
  ShieldOff,
  BadgeCheck,
  HandCoins,
  Target,
  Shield,
  Scale,
  CheckCircle,
} from "lucide-react";

const WarumCraftfindus = () => {
  const { t } = useTranslation();

  const problems = [
    { icon: AlertTriangle, title: t("warum.problem1Title"), desc: t("warum.problem1Desc") },
    { icon: DollarSign, title: t("warum.problem2Title"), desc: t("warum.problem2Desc") },
    { icon: Users, title: t("warum.problem3Title"), desc: t("warum.problem3Desc") },
    { icon: ShieldOff, title: t("warum.problem4Title"), desc: t("warum.problem4Desc") },
  ];

  const solutions = [
    { icon: Target, title: t("warum.solution1Title"), desc: t("warum.solution1Desc") },
    { icon: HandCoins, title: t("warum.solution2Title"), desc: t("warum.solution2Desc") },
    { icon: Users, title: t("warum.solution3Title"), desc: t("warum.solution3Desc") },
    { icon: Shield, title: t("warum.solution4Title"), desc: t("warum.solution4Desc") },
    { icon: BadgeCheck, title: t("warum.solution5Title"), desc: t("warum.solution5Desc") },
    { icon: Scale, title: t("warum.solution6Title"), desc: t("warum.solution6Desc") },
  ];

  return (
    <div className="min-h-screen bg-background" id="main-content">
      <SEOHead
        title={t("warum.seoTitle")}
        description={t("warum.seoDesc")}
        canonical="/warum-craftfindus"
      />
      <JsonLd
        includeService
        includeFaq
        breadcrumbs={[
          { name: "CRAFTFINDUS", url: "https://www.craftfindus.com/" },
          { name: t("warum.seoTitle"), url: "https://www.craftfindus.com/de/warum-craftfindus" },
        ]}
      />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 md:pb-20 section-padding text-center">
        <div className="max-w-3xl mx-auto space-y-6">
          <span className="text-micro tracking-[0.15em]">{t("warum.heroLabel")}</span>
          <h1 className="text-display">
            {t("warum.heroTitle1")}<br />
            <span className="text-accent">{t("warum.heroTitle2")}</span>
          </h1>
          <p className="text-caption text-lg max-w-xl mx-auto leading-relaxed">
            {t("warum.heroDesc")}
          </p>
        </div>
      </section>

      {/* Market Problem */}
      <section className="section-padding py-16 md:py-20 border-t border-border">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-micro tracking-[0.15em]">{t("warum.problemLabel")}</span>
            <h2 className="text-headline mt-4">{t("warum.problemTitle")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {problems.map((p, i) => (
              <div key={i} className="p-6 md:p-8 border border-destructive/20 bg-destructive/[0.03]">
                <div className="flex items-center gap-3 mb-4">
                  <p.icon className="w-5 h-5 text-destructive" />
                  <h3 className="text-subheadline">{p.title}</h3>
                </div>
                <p className="text-caption leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Solution */}
      <section className="section-padding py-16 md:py-20 border-t border-border">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-micro tracking-[0.15em]">{t("warum.solutionLabel")}</span>
            <h2 className="text-headline mt-4">{t("warum.solutionTitle")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutions.map((s, i) => (
              <div key={i} className="p-6 md:p-8 border border-accent/20 bg-accent/[0.02] hover:border-accent/40 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <s.icon className="w-5 h-5 text-accent" />
                  <h3 className="text-body font-medium">{s.title}</h3>
                </div>
                <p className="text-caption leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding py-20 border-t border-border text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="text-headline">{t("warum.ctaTitle")}</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <LocaleLink to="/inquiry">
              <Button variant="commit" size="lg" className="group w-full sm:w-auto">
                {t("warum.ctaClientBtn")}
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </LocaleLink>
            <LocaleLink to="/registrieren">
              <Button variant="quiet" size="lg" className="w-full sm:w-auto">
                {t("warum.ctaProviderBtn")}
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </LocaleLink>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WarumCraftfindus;
